import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, message } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { CheckCircleFilled } from '@ant-design/icons';
import { AppDispatch, RootState } from '../../store';
import { setAuth } from '../../store/slices/authSlice';
import { changeTemporaryPassword } from '../../services/authService';
import AuthPageLayout from '../../components/auth/AuthPageLayout';
import AuthFormContainer, { Header } from '../../components/auth/AuthFormContainer';
import { isLocalStorageAvailable, saveRememberMe } from '@/utils/storageUtils';
import ButtonGroup from '../../components/common/ButtonGroup';

const { Title, Text } = Typography;

const RequirementsContainer = styled.div`
  margin-top: 16px;
  margin-bottom: 28px;
`;

const RequirementTitle = styled.p`
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  color: #1F2937;
  margin: 0 0 8px 0;
`;

const RequirementsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const RequirementItem = styled.li<{ passed: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  color: ${props => props.passed ? '#0C827A' : '#848787'};
  margin-bottom: 8px;

  .anticon {
    font-size: 14px;
    color: ${props => props.passed ? '#0C827A' : '#848787'};
  }
`;

const ChangeTemporaryPasswordPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [form] = Form.useForm();
  const [password, setPassword] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const email = location.state?.email || '';
  const currentPassword = location.state?.currentPassword || '';
  const rememberMe = location.state?.rememberMe ?? false;

  useEffect(() => {
    if (!email || !currentPassword) {
      navigate('/auth/signin');
    }
  }, [email, currentPassword, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const hasMinLength = password.length >= 16;
  const hasNumber = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  const hasUpperAndLower = /[A-Z]/.test(password) && /[a-z]/.test(password);

  const handleFormChange = () => {
    const values = form.getFieldsValue(['password', 'confirmPassword']);
    const password = values.password?.trim() || '';
    const confirmPassword = values.confirmPassword?.trim() || '';
    const hasMinLength = password.length >= 16;
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    const hasUpperAndLower = /[A-Z]/.test(password) && /[a-z]/.test(password);
    const isPasswordsMatch = password === confirmPassword && password.length > 0;
    setIsFormValid(hasMinLength && hasNumber && hasSpecialChar && hasUpperAndLower && isPasswordsMatch);
  };

  const handleSubmit = async (values: { password: string; confirmPassword: string }) => {
    try {
      setLoading(true);
      
      // Change password (auth info is saved automatically in authService)
      const response = await changeTemporaryPassword(
        {
          email,
          password: values.password,
          passwordConfirmation: values.password,
          currentPassword,
        },
        rememberMe ?? false
      );
      
      // Save remember me preference
      if (isLocalStorageAvailable()) {
        saveRememberMe(email, rememberMe ?? false);
      }
      
      // Update Redux state
      dispatch(setAuth({ user: response.data.user }));
      
      message.success('Password changed successfully!');
    } catch (error: any) {
      // Case 1: Validation error - show inline error message
      if (error?.code === 'VALIDATION_ERROR' && error?.data) {
        const fieldErrors: any[] = [];
        
        if (error.data.password && Array.isArray(error.data.password)) {
          fieldErrors.push({
            name: 'password',
            errors: error.data.password,
          });
        }
        
        if (error.data.passwordConfirmation && Array.isArray(error.data.passwordConfirmation)) {
          fieldErrors.push({
            name: 'confirmPassword',
            errors: error.data.passwordConfirmation,
          });
        }
        
        if (fieldErrors.length > 0) {
          form.setFields(fieldErrors);
          return;
        }
      }
      
      // Case 2: Other errors - show toast directly
      message.error(error?.message || 'Failed to change password. Please try again.');
      console.error('Change password failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageLayout>
      <AuthFormContainer>
        <Header>
          <Title level={2}>Change temporary password</Title>
          <Text className="secondaryText">
            Create a new password for your account
          </Text>
        </Header>

        <Form
          form={form}
          name='changeTemporaryPassword'
          onFinish={handleSubmit}
          onValuesChange={handleFormChange}
          layout='vertical'
          autoComplete='off'
          requiredMark={false}
        >
          <Form.Item
            name='password'
            label='New password'
            style={{ marginBottom: 28 }}
            rules={[
              {
                required: true,
                message: 'Please input your new password!',
              },
            ]}
          >
            <Input.Password
              placeholder='*************'
              autoComplete='new-password'
              onChange={(e) => setPassword(e.target.value)}
            />
          </Form.Item>

          <Form.Item
            name='confirmPassword'
            label='Confirm password'
            style={{ marginBottom: 28 }}
            dependencies={['password']}
            rules={[
              {
                required: true,
                message: 'Please confirm your password!',
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The two passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password
              placeholder='*************'
              autoComplete='new-password'
            />
          </Form.Item>

          <RequirementsContainer>
            <RequirementTitle>Password must contain the following:</RequirementTitle>
            <RequirementsList>
              <RequirementItem passed={hasMinLength}>
                <CheckCircleFilled />
                <span>At least 16 characters</span>
              </RequirementItem>
              <RequirementItem passed={hasNumber}>
                <CheckCircleFilled />
                <span>At least one number</span>
              </RequirementItem>
              <RequirementItem passed={hasSpecialChar}>
                <CheckCircleFilled />
                <span>At least one special character</span>
              </RequirementItem>
              <RequirementItem passed={hasUpperAndLower}>
                <CheckCircleFilled />
                <span>At least one uppercase and lowercase character</span>
              </RequirementItem>
            </RequirementsList>
          </RequirementsContainer>
        </Form>

        <ButtonGroup>
          <Button
            type='primary'
            htmlType='submit'
            loading={loading}
            size='large'
            form='changeTemporaryPassword'
            disabled={!isFormValid || loading}
          >
            Confirm
          </Button>
        </ButtonGroup>
      </AuthFormContainer>
    </AuthPageLayout>
  );
};

export default ChangeTemporaryPasswordPage;

